import { useState } from 'react';
import { useTripContext } from '../trips/TripContext';
import {
  useCreateCountry,
  useDeleteCountry,
  useUpdateCountry,
} from '../trips/useTrips';

// Country tabs: the primary navigation axis within a trip (SPEC §1.1).
export function CountryTabs() {
  const { selectedTrip, countries, selectedCountry, selectCountry } = useTripContext();
  const [adding, setAdding] = useState(false);

  if (!selectedTrip) return null;

  return (
    <div className="mt-4">
      <ul className="nav nav-pills align-items-center flex-wrap">
        {countries.map((c) => (
          <li className="nav-item" key={c.id}>
            <button
              className={`nav-link ${c.id === selectedCountry?.id ? 'active' : ''}`}
              onClick={() => selectCountry(c.id)}
            >
              {c.name}
            </button>
          </li>
        ))}
        <li className="nav-item">
          <button className="btn btn-ghost btn-sm ms-1" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ Country'}
          </button>
        </li>
      </ul>

      {adding && <AddCountryForm tripId={selectedTrip.id} onDone={() => setAdding(false)} />}

      {selectedCountry && (
        <CountryActions tripId={selectedTrip.id} countryId={selectedCountry.id} name={selectedCountry.name} />
      )}
    </div>
  );
}

function AddCountryForm({ tripId, onDone }: { tripId: string; onDone: () => void }) {
  const { selectCountry } = useTripContext();
  const createCountry = useCreateCountry();
  const [name, setName] = useState('');
  const [isoCode, setIsoCode] = useState('');

  return (
    <form
      className="d-flex gap-2 flex-wrap py-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        createCountry.mutate(
          {
            tripId,
            input: {
              name: name.trim(),
              isoCode: isoCode.trim() ? isoCode.trim().toUpperCase() : undefined,
            },
          },
          {
            onSuccess: (country) => {
              selectCountry(country.id);
              onDone();
            },
          },
        );
      }}
    >
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 220 }}
        placeholder="Country name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        className="form-control form-control-sm"
        style={{ width: 100 }}
        placeholder="ISO (e.g. NZ)"
        value={isoCode}
        maxLength={2}
        onChange={(e) => setIsoCode(e.target.value)}
      />
      <button type="submit" className="btn btn-primary btn-sm" disabled={!name.trim() || createCountry.isPending}>
        {createCountry.isPending ? 'Adding…' : 'Add'}
      </button>
    </form>
  );
}

function CountryActions({
  tripId,
  countryId,
  name,
}: {
  tripId: string;
  countryId: string;
  name: string;
}) {
  const updateCountry = useUpdateCountry();
  const deleteCountry = useDeleteCountry();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  if (editing) {
    return (
      <form
        className="d-flex gap-2 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!draft.trim()) return;
          updateCountry.mutate(
            { tripId, countryId, patch: { name: draft.trim() } },
            { onSuccess: () => setEditing(false) },
          );
        }}
      >
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 220 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="btn btn-primary btn-sm">
          Save
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </form>
    );
  }

  return (
    <div className="d-flex gap-2 py-2">
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => {
          setDraft(name);
          setEditing(true);
        }}
      >
        Rename
      </button>
      <button
        className="btn btn-ghost btn-sm text-danger"
        onClick={() => {
          if (confirm(`Remove "${name}" from this trip?`)) deleteCountry.mutate({ tripId, countryId });
        }}
      >
        Remove
      </button>
    </div>
  );
}
